import { useState } from 'react';
import { MarketingPage } from '../types';
import { useMarketingData } from '../MarketingContext';

interface Props { onNavigate: (page: MarketingPage) => void; }

export default function Dashboard({ onNavigate }: Props) {
  const { companies, projects, products, campaigns, content, tasks, analytics, keywords, research, notifications } = useMarketingData();
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterChannel, setFilterChannel] = useState('all');

  const activeCampaigns = campaigns.filter(c => c.status === 'in_progress' || c.status === 'planned');

  const totalBudget = companies.reduce((s, c) => s + c.budget, 0);
  const totalSpend = analytics.reduce((s, a) => s + a.spend, 0);
  const totalLeads = analytics.reduce((s, a) => s + a.leads, 0);
  const totalConversions = analytics.reduce((s, a) => s + a.conversions, 0);

  const overdueTasks = tasks.filter(t => new Date(t.dueDate) < new Date() && t.status !== 'completed');
  const upcomingDeadlines = tasks.filter(t => {
    const due = new Date(t.dueDate);
    const now = new Date();
    const week = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return due > now && due < week && t.status !== 'completed';
  });

  const kpiCards = [
    { label: 'Companies', value: companies.length, icon: '🏢', color: 'bg-blue-500', onClick: () => onNavigate('marketing-companies') },
    { label: 'Projects', value: projects.length, icon: '📋', color: 'bg-indigo-500', onClick: () => onNavigate('marketing-projects') },
    { label: 'Products', value: products.length, icon: '📦', color: 'bg-violet-500', onClick: () => onNavigate('marketing-products') },
    { label: 'Active Campaigns', value: activeCampaigns.length, icon: '📣', color: 'bg-emerald-500', onClick: () => onNavigate('marketing-campaigns') },
    { label: 'Marketing Budget', value: `₹${(totalBudget / 100000).toFixed(1)}L`, icon: '💰', color: 'bg-amber-500', onClick: () => onNavigate('marketing-analytics') },
    { label: 'Actual Spend', value: `₹${(totalSpend / 100000).toFixed(1)}L`, icon: '💳', color: 'bg-rose-500', onClick: () => onNavigate('marketing-analytics') },
    { label: 'Leads Generated', value: totalLeads, icon: '🎯', color: 'bg-teal-500', onClick: () => onNavigate('marketing-analytics') },
    { label: 'Conversions', value: totalConversions, icon: '✅', color: 'bg-green-500', onClick: () => onNavigate('marketing-analytics') },
    { label: 'Content Items', value: content.length, icon: '📝', color: 'bg-cyan-500', onClick: () => onNavigate('marketing-content') },
    { label: 'SEO Keywords', value: keywords.length, icon: '🔍', color: 'bg-purple-500', onClick: () => onNavigate('marketing-seo') },
    { label: 'Research', value: research.length, icon: '🔬', color: 'bg-pink-500', onClick: () => onNavigate('marketing-research') },
    { label: 'Open Tasks', value: tasks.filter(t => t.status !== 'completed').length, icon: '✅', color: 'bg-orange-500', onClick: () => onNavigate('marketing-tasks') },
  ];

  // Channel performance
  const channelData = ['seo', 'ppc', 'social_media', 'content', 'email'].map(ch => {
    const chAnalytics = analytics.filter(a => a.channel === ch);
    return {
      channel: ch,
      leads: chAnalytics.reduce((s, a) => s + a.leads, 0),
      revenue: chAnalytics.reduce((s, a) => s + a.revenue, 0),
      spend: chAnalytics.reduce((s, a) => s + a.spend, 0),
    };
  });
  const maxChannelLeads = Math.max(...channelData.map(c => c.leads), 1);

  // Company performance
  const companyPerf = companies.map(co => ({
    ...co,
    projectCount: projects.filter(p => p.companyId === co.id).length,
    campaignCount: campaigns.filter(c => c.companyId === co.id).length,
    productCount: products.filter(p => p.companyId === co.id).length,
  }));

  // Content by status
  const contentByStatus = ['draft', 'in_review', 'published', 'scheduled', 'idea'].map(status => ({
    status,
    count: content.filter(c => c.status === status).length,
  }));
  const maxContentStatus = Math.max(...contentByStatus.map(c => c.count), 1);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Executive Marketing Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Complete overview of all marketing activity across companies, projects, and campaigns</p>
        </div>
        <div className="text-xs text-gray-400">Last updated: {new Date().toLocaleDateString('en-IN')}</div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white p-3 rounded-xl border border-gray-200">
        <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500">
          <option value="all">All Companies</option>
          {companies.map(co => <option key={co.id} value={co.id}>{co.name}</option>)}
        </select>
        <select value={filterChannel} onChange={e => setFilterChannel(e.target.value)} className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-blue-500">
          <option value="all">All Channels</option>
          <option value="seo">SEO</option><option value="ppc">PPC</option><option value="social_media">Social Media</option>
          <option value="content">Content</option><option value="email">Email</option>
        </select>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
        {kpiCards.map((kpi, i) => (
          <button key={i} onClick={kpi.onClick} className="bg-white border border-gray-200 rounded-xl p-4 text-left hover:shadow-md transition-shadow group">
            <div className="flex items-center gap-2 mb-2">
              <div className={`h-8 w-8 rounded-lg ${kpi.color} flex items-center justify-center text-white text-sm`}>{kpi.icon}</div>
              <span className="text-xs font-medium text-gray-500">{kpi.label}</span>
            </div>
            <div className="text-2xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{kpi.value}</div>
          </button>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Channel Performance */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Channel Performance (Leads)</h3>
          <div className="space-y-3">
            {channelData.map(ch => (
              <div key={ch.channel}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-gray-600 capitalize">{ch.channel.replace('_', ' ')}</span>
                  <span className="font-medium text-gray-900">{ch.leads} leads</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${(ch.leads / maxChannelLeads) * 100}%` }} />
                </div>
                {ch.revenue > 0 && <div className="text-[10px] text-gray-400 mt-0.5">Revenue: ₹{(ch.revenue / 1000).toFixed(0)}K | Spend: ₹{(ch.spend / 1000).toFixed(0)}K</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Content Pipeline */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Content Pipeline</h3>
          <div className="space-y-3">
            {contentByStatus.map(cs => {
              const colors: Record<string, string> = {
                draft: 'bg-amber-400', in_review: 'bg-blue-400', published: 'bg-green-400', scheduled: 'bg-purple-400', idea: 'bg-gray-300'
              };
              return (
                <div key={cs.status}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-gray-600 capitalize">{cs.status.replace('_', ' ')}</span>
                    <span className="font-medium text-gray-900">{cs.count}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full ${colors[cs.status] || 'bg-gray-400'} rounded-full`} style={{ width: `${(cs.count / maxContentStatus) * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <button onClick={() => onNavigate('marketing-content')} className="mt-3 text-xs text-blue-600 hover:underline">View Content Calendar →</button>
        </div>

        {/* SEO Overview */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">SEO Keyword Tracking</h3>
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-green-50 rounded-lg p-2">
                <div className="text-lg font-bold text-green-700">{keywords.filter(k => k.currentRank > 0 && k.currentRank <= 10).length}</div>
                <div className="text-[10px] text-green-600">Top 10</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-2">
                <div className="text-lg font-bold text-amber-700">{keywords.filter(k => k.currentRank > 10 && k.currentRank <= 30).length}</div>
                <div className="text-[10px] text-amber-600">Top 30</div>
              </div>
              <div className="bg-red-50 rounded-lg p-2">
                <div className="text-lg font-bold text-red-700">{keywords.filter(k => k.currentRank === 0).length}</div>
                <div className="text-[10px] text-red-600">Not Ranking</div>
              </div>
            </div>
            <div className="space-y-1.5 mt-3">
              {keywords.slice(0, 5).map(kw => (
                <div key={kw.id} className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 truncate max-w-[150px]">{kw.keyword}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">Vol: {kw.volume}</span>
                    <span className={`font-medium ${kw.currentRank > 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {kw.currentRank > 0 ? `#${kw.currentRank}` : '—'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <button onClick={() => onNavigate('marketing-seo')} className="mt-3 text-xs text-blue-600 hover:underline">View SEO Center →</button>
        </div>
      </div>

      {/* Company Performance + Activity + Tasks Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Company Performance */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Company Performance</h3>
          <div className="space-y-3">
            {companyPerf.map(co => (
              <button key={co.id} onClick={() => onNavigate('marketing-companies')} className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left">
                <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center text-lg">{co.logo}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{co.name}</div>
                  <div className="text-xs text-gray-500">
                    {co.projectCount} projects · {co.campaignCount} campaigns · {co.productCount} products
                  </div>
                </div>
                <div className="text-xs font-medium text-blue-600">₹{(co.budget / 100000).toFixed(1)}L</div>
              </button>
            ))}
          </div>
        </div>

        {/* Activity Timeline */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {[
              { icon: '📋', text: 'Project "SKI Digital Transformation" moved to In Progress', time: '2 hours ago', color: 'bg-blue-100' },
              { icon: '📣', text: 'Campaign "Google Ads" status updated to Planned', time: '5 hours ago', color: 'bg-green-100' },
              { icon: '📝', text: 'Blog article "Complete Guide to Castor Wheel Selection" moved to Draft', time: '1 day ago', color: 'bg-amber-100' },
              { icon: '🔬', text: 'Research "Competitor Digital Analysis" verified', time: '2 days ago', color: 'bg-purple-100' },
              { icon: '🏢', text: 'Company "SCPL Kolkata" added to portal', time: '3 days ago', color: 'bg-teal-100' },
              { icon: '🔍', text: '8 SEO keywords added for tracking', time: '3 days ago', color: 'bg-pink-100' },
              { icon: '📏', text: 'Standard "IS 15488" compliance reviewed', time: '4 days ago', color: 'bg-orange-100' },
              { icon: '📰', text: 'Blog article saved for reference', time: '5 days ago', color: 'bg-indigo-100' },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className={`h-7 w-7 rounded-full ${item.color} flex items-center justify-center text-xs flex-shrink-0 mt-0.5`}>{item.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-700 leading-relaxed">{item.text}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{item.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tasks & Notifications */}
        <div className="space-y-4">
          {/* Upcoming Deadlines */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">⏰ Upcoming Deadlines (7 days)</h3>
            {upcomingDeadlines.length === 0 ? (
              <div className="text-xs text-gray-400 text-center py-3">No upcoming deadlines</div>
            ) : (
              <div className="space-y-2">
                {upcomingDeadlines.map(t => (
                  <div key={t.id} className="flex items-center gap-2 p-2 bg-amber-50 rounded-lg">
                    <div className="text-amber-600 text-xs">⚠️</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-800 truncate">{t.title}</div>
                      <div className="text-[10px] text-amber-600">Due: {new Date(t.dueDate).toLocaleDateString('en-IN')}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Overdue Tasks */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">🚨 Overdue Tasks</h3>
            {overdueTasks.length === 0 ? (
              <div className="text-xs text-gray-400 text-center py-3">No overdue tasks 🎉</div>
            ) : (
              <div className="space-y-2">
                {overdueTasks.map(t => (
                  <div key={t.id} className="flex items-center gap-2 p-2 bg-red-50 rounded-lg">
                    <div className="text-red-500 text-xs">❌</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-800 truncate">{t.title}</div>
                      <div className="text-[10px] text-red-500">Due: {new Date(t.dueDate).toLocaleDateString('en-IN')}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Unread Notifications */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">🔔 Notifications</h3>
            <div className="space-y-2">
              {notifications.filter(n => !n.read).map(n => (
                <div key={n.id} className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg">
                  <div className="text-blue-500 text-xs mt-0.5">
                    {n.type === 'deadline' ? '⏰' : n.type === 'assignment' ? '👤' : '📝'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-gray-800">{n.title}</div>
                    <div className="text-[10px] text-gray-500">{n.message}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {([
          { label: 'Companies', icon: '🏢', page: 'marketing-companies' as MarketingPage },
          { label: 'Projects', icon: '📋', page: 'marketing-projects' as MarketingPage },
          { label: 'Products', icon: '📦', page: 'marketing-products' as MarketingPage },
          { label: 'Strategy', icon: '🎯', page: 'marketing-strategy' as MarketingPage },
          { label: 'Campaigns', icon: '📣', page: 'marketing-campaigns' as MarketingPage },
          { label: 'SEO', icon: '🔍', page: 'marketing-seo' as MarketingPage },
          { label: 'Research', icon: '🔬', page: 'marketing-research' as MarketingPage },
          { label: 'Tasks', icon: '✅', page: 'marketing-tasks' as MarketingPage },
        ]).map(item => (
          <button key={item.page} onClick={() => onNavigate(item.page)} className="bg-white border border-gray-200 rounded-xl p-3 text-center hover:shadow-md hover:border-blue-200 transition-all group">
            <div className="text-2xl mb-1">{item.icon}</div>
            <div className="text-xs font-medium text-gray-700 group-hover:text-blue-600">{item.label}</div>
          </button>
        ))}
      </div>
    </div>
  );
}
